import { useState, useRef } from "react";
import { MapPin, Calendar, Clock, Users, Euro, Edit3, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/services/api";
import { useSettings } from "@/context/SettingsContext";
import { setupPushNotifications } from "@/services/push";

/**
 * Reusable match creation form used by the dedicated /creer-un-match page
 * (CreateMatchPage). `initialLocation` pre-fills the "Lieu" field; `onCreated`
 * is called with the new match on success.
 */
export default function CreateMatchForm({ initialLocation = "", onCreated }) {
  const { max_players_limit: maxLimit = 22, max_days_advance: maxDays = 14 } = useSettings();
  const todayStr = new Date().toISOString().slice(0, 10);
  const maxDateStr = new Date(Date.now() + maxDays * 86400000).toISOString().slice(0, 10);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    location: initialLocation,
    date: "",
    time: "",
    max_players: "10",
    price: "",
    is_private: false,
  });
  const [terrainCoords, setTerrainCoords] = useState(null); // [lng, lat] when picked
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customCity, setCustomCity] = useState("");
  const debounceRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "location") {
      setTerrainCoords(null);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!value.trim()) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        try {
          const list = await api.terrainsSearch(value, 8);
          setSuggestions(Array.isArray(list) ? list : []);
          setShowSuggestions(true);
        } catch {
          setSuggestions([]);
        }
      }, 200);
    }
  };

  const handlePickTerrain = (t) => {
    const label = t.ville ? `${t.nom} (${t.ville})` : t.nom;
    setFormData((prev) => ({ ...prev, location: label }));
    const coords = t.coordonnees?.coordinates;
    setTerrainCoords(coords && coords.length === 2 ? coords : null);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Best-effort: prompt for notifications (native popup on this user gesture),
    // but NEVER block match creation if push is denied/unsupported (incognito,
    // iOS Safari tab, etc.).
    try { await setupPushNotifications(); } catch (_) {}

    if (!formData.title || !formData.location || !formData.date || !formData.time) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setIsLoading(true);

    try {
      const newMatch = await api.createMatch({
        title: formData.title,
        location: formData.location,
        date: formData.date,
        time: formData.time,
        max_players: parseInt(formData.max_players) || 10,
        price: formData.price || "Gratuit",
        is_private: formData.is_private,
        ...(terrainCoords ? { coordinates: terrainCoords } : {}),
      });

      toast.success("Match cree avec succes!");
      setFormData({
        title: "",
        location: "",
        date: "",
        time: "",
        max_players: "10",
        price: "",
        is_private: false,
      });
      if (onCreated) onCreated(newMatch);
    } catch (error) {
      toast.error(error.message || "Erreur lors de la creation");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-5 pt-4">
      <div className="space-y-1.5">
        <Label htmlFor="title" className="text-xs font-medium text-gray-400">Titre du match *</Label>
        <Input
          data-testid="match-title-input"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Ex: Foot 5v5 - Soiree detente"
          className="h-11 w-full rounded-xl bg-black border-[#333] text-white text-sm placeholder:text-gray-600"
        />
      </div>

      <div className="space-y-1.5">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <Label htmlFor="location" className="text-xs font-medium text-gray-400 flex items-center gap-1.5">
            <MapPin size={12} className="text-[#22c55e]" /> Lieu *
          </Label>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {customMode && (
              <span
                data-testid="custom-mode-badge"
                style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "#a1a1aa",
                  background: "#18181b",
                  border: "1px solid #3f3f46",
                  padding: "2px 8px", borderRadius: 999,
                }}
              >
                Saisie libre
              </span>
            )}
            <button
              type="button"
              data-testid="toggle-custom-terrain"
              onClick={() => {
                setCustomMode((prev) => {
                  const next = !prev;
                  if (next) {
                    setTerrainCoords(null);
                    setFormData((p) => ({ ...p, location: "" }));
                  } else {
                    setCustomName("");
                    setCustomCity("");
                  }
                  return next;
                });
              }}
              style={{
                background: "transparent", border: "none",
                color: "#22c55e", fontSize: 11, fontWeight: 700,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
              }}
            >
              <Edit3 size={11} />
              {customMode ? "Choisir dans la liste" : "Autre terrain"}
            </button>
          </div>
        </div>

        {!customMode ? (
          <div style={{ position: "relative" }}>
            <Input
              data-testid="match-location-input"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              onFocus={() => formData.location && suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              autoComplete="off"
              placeholder="Tape le nom du terrain (ex: Le Five)"
              className="h-11 w-full rounded-xl bg-black border-[#333] text-white text-sm placeholder:text-gray-600"
            />
            {showSuggestions && suggestions.length > 0 && (
              <ul
                data-testid="location-suggestions"
                style={{
                  position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                  width: "100%", boxSizing: "border-box",
                  background: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: 12,
                  maxHeight: 240, overflow: "auto",
                  zIndex: 60,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                  listStyle: "none", padding: 4, margin: 0,
                }}
              >
                {suggestions.map((t, idx) => (
                  <li
                    key={`${idx}-${t.nom}-${t.ville || ""}`}
                    data-testid={`suggestion-${t.nom}`}
                    onMouseDown={(e) => { e.preventDefault(); handlePickTerrain(t); }}
                    style={{
                      padding: "10px 12px", borderRadius: 8,
                      color: "#fff", fontSize: 13, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 8,
                    }}
                    className="hover:bg-[#1a1a1a]"
                  >
                    <MapPin size={13} style={{ color: "#22c55e", flexShrink: 0 }} />
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.nom} ({t.ville || t.adresse || t.commune || "Adresse inconnue"})
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {terrainCoords && (
              <span data-testid="terrain-coords-pill" style={{
                position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                fontSize: 10, fontWeight: 700, color: "#22c55e",
                background: "rgba(34,197,94,0.12)", padding: "3px 8px",
                borderRadius: 999, border: "1px solid rgba(34,197,94,0.35)",
              }}>
                GPS OK
              </span>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Input
              data-testid="custom-terrain-name-input"
              placeholder="Nom du terrain"
              value={customName}
              onChange={(e) => {
                const v = e.target.value;
                setCustomName(v);
                setFormData((p) => ({
                  ...p,
                  location: customCity ? `${v} (${customCity})` : v,
                }));
              }}
              className="h-11 w-full rounded-xl bg-black border-[#333] text-white text-sm placeholder:text-gray-600"
            />
            <Input
              data-testid="custom-terrain-city-input"
              placeholder="Ville"
              value={customCity}
              onChange={(e) => {
                const v = e.target.value;
                setCustomCity(v);
                setFormData((p) => ({
                  ...p,
                  location: v ? `${customName} (${v})` : customName,
                }));
              }}
              className="h-11 w-full rounded-xl bg-black border-[#333] text-white text-sm placeholder:text-gray-600"
            />
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="date" className="text-xs font-medium text-gray-400 flex items-center gap-1.5">
          <Calendar size={12} className="text-[#22c55e]" /> Date *
        </Label>
        <Input
          data-testid="match-date-input"
          id="date"
          name="date"
          type="date"
          min={todayStr}
          max={maxDateStr}
          value={formData.date}
          onChange={handleChange}
          className="h-11 w-full rounded-xl bg-black border-[#333] text-white text-sm"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="time" className="text-xs font-medium text-gray-400 flex items-center gap-1.5">
          <Clock size={12} className="text-[#22c55e]" /> Heure *
        </Label>
        <input
          data-testid="match-time-input"
          id="time"
          name="time"
          type="time"
          step="300"
          value={formData.time}
          onChange={handleChange}
          required
          className="w-full rounded-xl bg-black border border-[#333] text-white px-4 focus:ring-2 focus:ring-[#22c55e] focus:outline-none"
          style={{
            fontSize: '16px',
            minHeight: '50px',
            pointerEvents: 'auto',
            userSelect: 'auto',
            touchAction: 'manipulation',
          }}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="max_players" className="text-xs font-medium text-gray-400 flex items-center gap-1.5">
          <Users size={12} className="text-[#22c55e]" /> Nombre max de joueurs
        </Label>
        <Input
          data-testid="match-maxplayers-input"
          id="max_players"
          name="max_players"
          type="number"
          min="2"
          max={String(maxLimit)}
          value={formData.max_players}
          onChange={handleChange}
          placeholder="10"
          className="h-11 w-full rounded-xl bg-black border-[#333] text-white text-sm placeholder:text-gray-600"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="price" className="text-xs font-medium text-gray-400 flex items-center gap-1.5">
          <Euro size={12} className="text-[#22c55e]" /> Prix par joueur
        </Label>
        <Input
          data-testid="match-price-input"
          id="price"
          name="price"
          value={formData.price}
          onChange={handleChange}
          placeholder="Ex: 8 EUR ou Gratuit"
          className="h-11 w-full rounded-xl bg-black border-[#333] text-white text-sm placeholder:text-gray-600"
        />
      </div>

      <button
        type="button"
        data-testid="toggle-private-match"
        onClick={() => setFormData((p) => ({ ...p, is_private: !p.is_private }))}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, padding: "12px 14px", borderRadius: 12,
          background: "#0a0a0a", border: `1px solid ${formData.is_private ? "#22c55e" : "#333"}`,
          cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, textAlign: "left" }}>
          <Lock size={14} className="text-[#22c55e]" />
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>Match prive</p>
            <p style={{ fontSize: 11, color: "#6b7280" }}>Sur invitation — rejoignable via le lien uniquement</p>
          </div>
        </div>
        <span
          data-testid="private-switch"
          style={{
            width: 42, height: 24, borderRadius: 999, flexShrink: 0,
            background: formData.is_private ? "#22c55e" : "#333",
            position: "relative", transition: "background-color 0.2s",
          }}
        >
          <span style={{
            position: "absolute", top: 2, left: formData.is_private ? 20 : 2,
            width: 20, height: 20, borderRadius: "50%", background: "#fff",
            transition: "left 0.2s",
          }} />
        </span>
      </button>

      <Button
        data-testid="submit-create-match"
        type="submit"
        disabled={isLoading}
        className="w-full h-11 rounded-xl bg-[#22c55e] hover:bg-[#16a34a] text-white font-semibold text-sm transition-all active:scale-[0.98] mt-2 disabled:opacity-50"
      >
        {isLoading ? "Creation..." : "Creer le match"}
      </Button>
    </form>
  );
}
